import DatasetLinked from "@mui/icons-material/DatasetLinked";
import HubOutlinedIcon from "@mui/icons-material/HubOutlined";
import JoinInnerOutlinedIcon from "@mui/icons-material/JoinInnerOutlined";
import ContentCut from '@mui/icons-material/ContentCut';
import LaunchIcon from "@mui/icons-material/Launch";
import {
  Box,
  Button,
  Divider,
  Drawer,
  Link,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Menu,
  MenuItem,
  Typography,
  useMediaQuery,
} from "@mui/material";
import { useTheme } from "@mui/material/styles";
import PropTypes from "prop-types";
import { memo, useState } from "react";
import {
  NavLink,
  useLocation,
  useNavigate,
  useParams,
  useRouteLoaderData,
} from "react-router-dom";

import ProjectRoleLabel from "components/ProjectRoleLabel";
import useLayoutState from "hooks/useLayoutState";
import getCurrentUser from "hooks/getCurrentUser";
import getConfig from "hooks/getConfig";
import { drawerWidth } from "themes/constants";
import { USER_ROLE_LEVELS, USER_ROLES } from 'constants/constants';

export default memo(Sidebar);

function Sidebar({ window = null }) {
  Sidebar.propTypes = {
    window: PropTypes.object,
  };

  const theme = useTheme();
  const {
    isSidebarDrawerOpen,
    toggleIsSidebarDrawerOpen,
    setIsSidebarDrawerOpen,
  } = useLayoutState();

  const loggedInUser = getCurrentUser();
  const { partners } = loggedInUser;
  const { activeProjectDetails } = useRouteLoaderData("projectRoot");
  const matchUpMd = useMediaQuery(theme.breakpoints.up("md"));
  const { projectId } = useParams();
  const { pathname } = useLocation();
  const navigate = useNavigate();
  const { FLAGS } = getConfig();
  const showListCutter = FLAGS?.LIST_CUTTER;

  const [anchorEl, setAnchorEl] = useState(null);
  const isProjectMenuOpen = Boolean(anchorEl);

  const container = window ? () => window.document.body : undefined;

  const { project, roles } = activeProjectDetails;
  const defaultProjectNameText = "No project name";
  const { name: displayName } = project || { name: false };
  const { targetsmart_matching: targetsmartMatchingEnabled, catalist_matching: catalistMatchingEnabled } = project;
  const userActiveRoles = loggedInUser.roles.filter(role => role.partner_gcp_project_id === projectId);
  const adminOrEditor = userActiveRoles.filter(role =>
    USER_ROLE_LEVELS[role.level] === USER_ROLES.ADMIN ||
    USER_ROLE_LEVELS[role.level] === USER_ROLES.EDITOR
  ).length > 0;

  let visibleNavItems = [
    {
      to: `/projects/${projectId}/syncs/managed`,
      label: "Syncs",
      icon: <HubOutlinedIcon />,
    },
    // TODO: Use Feature Flags to control this instead of superusers/whitelist
    ...(showListCutter && adminOrEditor)
      ? [{
          to: `/projects/${projectId}/list-cutter`,
          label: "List Cutter (Preview)",
          icon: <ContentCut />,
        }]
      : [],
  ];
  if (loggedInUser?.is_superuser) {
    visibleNavItems.push({
      to: `/projects/${projectId}/crosstabs`,
      label: "Crosstabs",
      icon: <IconLayoutGrid />,
    });
  }
  // Make sure matching is enabled at the partner level for at least one of the vendors
  // before adding matching jobs to the side nav
  if ((targetsmartMatchingEnabled || catalistMatchingEnabled)){
    visibleNavItems.push({
      to: `/projects/${projectId}/matching-jobs`,
      label: "Matching Jobs",
      icon: <JoinInnerOutlinedIcon />,
    })
  }

  const activeLinkStyles = {
    backgroundColor: theme.palette.secondary.light,
    color: theme.palette.secondary.main,
  };

  const onClickMenuItem = () => {
    if (!matchUpMd) setIsSidebarDrawerOpen(false);
  };

  const onClickSwitchProject = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const onClickDifferentProject = (newProjectId) => {
    setAnchorEl(null);
    const targetRoute = pathname?.replace(projectId, newProjectId);

    if (targetRoute) {
      navigate(targetRoute);
    }
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const navList = visibleNavItems?.length ? (
    <List>
      {visibleNavItems?.map((visibleRoute, idx) => (
        <ListItem key={idx}>
          <ListItemButton
            component={NavLink}
            to={visibleRoute.to}
            style={({ isActive }) => (isActive ? activeLinkStyles : undefined)}
            onClick={onClickMenuItem}
          >
            <ListItemIcon
              sx={{
                my: "auto",
                color: "inherit",
                minWidth: !visibleRoute?.icon ? 18 : 36,
              }}
            >
              {visibleRoute.icon}
            </ListItemIcon>
            <ListItemText
              primary={
                <Typography variant={"h5"} color="inherit">
                  {visibleRoute.label}
                </Typography>
              }
            />
          </ListItemButton>
        </ListItem>
      ))}
    </List>
  ) : null;

  return (
    <Box
      component="nav"
      sx={{ flexShrink: { md: 0 }, width: matchUpMd ? drawerWidth : "auto" }}
      aria-label="Primary navigation"
    >
      <Drawer
        container={container}
        variant={matchUpMd ? "persistent" : "temporary"}
        anchor="left"
        open={isSidebarDrawerOpen}
        onClose={() => toggleIsSidebarDrawerOpen()}
        sx={{
          "& .MuiDrawer-paper": {
            width: drawerWidth,
            background: theme.palette.background.default,
            color: theme.palette.text.primary,
            borderRight: "none",
            height: !matchUpMd ? "calc(100vh - 56px)" : "calc(100vh - 88px)",
            paddingLeft: "16px",
            paddingRight: "16px",
            [theme.breakpoints.up("md")]: {
              top: "75px",
            },
            [theme.breakpoints.down("md")]: {
              paddingTop: "20px",
            },
          },
        }}
        ModalProps={{ keepMounted: true }}
        color="inherit"
      >
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            mt: 1,
            ml: 1,
          }}
        >
          <Typography variant="h5">ACTIVE PROJECT</Typography>
          {partners.length > 1 && (
            <>
              <Button
                id="basic-button"
                aria-controls={isProjectMenuOpen ? "basic-menu" : undefined}
                aria-haspopup="true"
                aria-expanded={isProjectMenuOpen ? "true" : undefined}
                onClick={onClickSwitchProject}
              >
                Switch Project
              </Button>
              <Menu
                id="basic-menu"
                anchorEl={anchorEl}
                open={isProjectMenuOpen}
                onClose={handleClose}
                MenuListProps={{
                  "aria-labelledby": "basic-button",
                }}
              >
                {partners.length > 1 && partners
                  .filter((project) => project.id !== projectId)
                  .map(({ name, gcp_project_id }) => (
                    <MenuItem
                      key={gcp_project_id}
                      onClick={() => onClickDifferentProject(gcp_project_id)}
                    >
                      <ListItemIcon>
                        <DatasetLinked />
                      </ListItemIcon>
                      <ListItemText
                        primary={
                          <>
                            {name ?? defaultProjectNameText}
                          </>
                        }
                        secondary={gcp_project_id}
                      />
                    </MenuItem>
                  ))}
              </Menu>
            </>
          )}
        </Box>
        <Box
          sx={{
            pl: 2,
            pr: 1,
            borderColor: "grey.400",
            display: "flex",
            alignItems: "center",
          }}
        >
          <DatasetLinked />
          <Box sx={{ ml: 1 }}>
            <Box sx={{ display: "flex", alignItems: "center", mb: "3px" }}>
              {displayName && (
                <Typography
                  sx={{
                    fontWeight: 700,
                    display: "flex",
                    alignItems: "center",
                    color: "grey.800",
                  }}
                >
                  {displayName ?? defaultProjectNameText}
                </Typography>
              )}
              {roles && <ProjectRoleLabel projectRoles={roles} />}
            </Box>
            <Typography sx={{ mb: "5px" }}>{projectId}</Typography>
            {projectId && loggedInUser?.email && (
              <Link
                href={`https://console.cloud.google.com/bigquery/?p=${projectId}&project=${projectId}&authUser=${loggedInUser.email}`}
                target="_blank"
                sx={{ display: "flex", alignItems: "center", fontSize: "14px" }}
              >
                View PAD in BigQuery
                <LaunchIcon sx={{ ml: 1, fontSize: "14px" }} />
              </Link>
            )}
          </Box>
        </Box>
        <Divider sx={{ mt: 2 }} />
        {navList}
      </Drawer>
    </Box>
  );
}
